/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { BlockSnapshot } from "@vienna/common";

import { Block } from "src/classes/block";
import { Point, Rectangle } from "src/classes/plate/lib/types";
import { MathUtils } from "src/classes/utilities/MathUtils";
import { ONE_GRID_UNIT } from "src/lib/constants"; //@todo move constants

export type CSSPosition = {
  top: number;
  left: number;
  width: number;
  height: number;
};
export class PositionUtils {
  public static snapToGrid = (num: number): number => {
    return MathUtils.roundNumToNearest(num, ONE_GRID_UNIT);
  };

  public static getCSSPositionFromPoints(p1: Point, p2: Point): CSSPosition {
    const rectangle = MathUtils.getRectangleFromPoints(p1, p2);
    const top = PositionUtils.snapToGrid(rectangle.top);
    const left = PositionUtils.snapToGrid(rectangle.left);
    const width = PositionUtils.snapToGrid(Math.abs(rectangle.left - rectangle.right)) + 1; // @todo @plusone
    const height = PositionUtils.snapToGrid(Math.abs(rectangle.top - rectangle.bottom)) + 1; // @todo @plusone
    return { top, left, width, height };
  }

  /**
   * Gets the max distance a (group of) block(s) can go in any direction.
   *
   * ```
   * ┌───────────plate───────────┐
   * │         ▲                 │
   * │         │ up              │
   * │         ▼                 │
   * │     ┌─block─┐             │
   * │◀─left       │◀──right ───▶│
   * │     └───────┘             │
   * │         ▲                 │
   * │         │ down            │
   * │         ▼                 │
   * └───────────────────────────┘
   * ```
   */
  public static getMaxMoveForBlocks = (
    blocks: BlockSnapshot[],
    plateDimensions: { width: number; height: number }
  ): { up: number; down: number; left: number; right: number } => {
    const collectiveSize = PositionUtils.getSizeOfBlockGroup(blocks);
    const plateBoundaries = { top: 0, left: 0, right: plateDimensions.width, bottom: plateDimensions.height };
    const spaceBetween = PositionUtils.getSpaceBetweenRectangles(collectiveSize, plateBoundaries);
    return {
      up: spaceBetween.top,
      left: spaceBetween.left,
      right: Math.abs(spaceBetween.right - 1),
      down: Math.abs(spaceBetween.bottom - 1), //@todo @plusone
    };
  };

  /**
   * Returns a rectangle representing the collective size of all the blocks
   */
  private static getSizeOfBlockGroup = (blocks: BlockSnapshot[]): Rectangle => {
    const top = Math.min(...blocks.map((block) => block.top));
    const left = Math.min(...blocks.map((block) => block.left));
    const right = Math.max(...blocks.map((block) => block.left + block.width));
    const bottom = Math.max(...blocks.map((block) => block!.top + block.height));
    return { top, left, right, bottom };
  };

  /**
   * Takes two rectangle objects and returns the space in between each of their sides.
   *
   * ```
   * ┌───────────rectB───────────┐
   * │             │             │
   * │             │ top         │
   * │             │             │
   * │       ┌───rectA───┐       │
   * │  left │           │ right │
   * │───────│           │───────│
   * │       │           │       │
   * │       └───────────┘       │
   * │             │             │
   * │             │ bottom      │
   * │             │             │
   * └───────────────────────────┘
   * ```
   */
  public static getSpaceBetweenRectangles = (rectA: Rectangle, rectB: Rectangle): Rectangle => {
    const top = rectA.top - rectB.top;
    const left = rectA.left - rectB.left;
    const right = rectA.right - rectB.right;
    const bottom = rectA.bottom - rectB.bottom;
    return { top, left, right, bottom };
  };

  public static sortBlocksBySize(blocks: Block[]): Block[] {
    return blocks.sort((a, b) =>
      a.snapshot.height * a.snapshot.width > b.snapshot.height * b.snapshot.width ? -1 : 1
    );
  }

  public static rectsOverlap = (rectA: Rectangle, rectB: Rectangle): boolean => {
    // If one rectangle is on left side of other
    if (rectA.left >= rectB.right || rectB.left >= rectA.right) {
      return false;
    }
    // If one rectangle is above other
    if (rectA.top >= rectB.bottom || rectB.top >= rectA.bottom) {
      return false;
    }

    return true;
  };
}
